import { ContentChild, Directive, ElementRef, HostBinding, HostListener, Input } from '@angular/core';
import { BaseDirective } from '@portal-shared/app/components/base-components/base.component';
import { BaseDraggingService } from '@portal-shared/app/services/base-dragging.service';
import { CssUtils } from '@portal-shared/app/utils/css.utils';
import { EventUtils } from '@portal-shared/app/utils/event.utils';
import { TimerUtils } from '@portal-shared/app/utils/timer.utils';
import { takeWhile } from 'rxjs';

@Directive({
  standalone: true,
  selector: '[portalDraggable]'
})
export class DraggableDirective extends BaseDirective {
  @Input('portalDraggable')
  public payload?: unknown;

  @HostBinding('class.draggable')
  @Input()
  public isDraggable = true;

  @HostBinding('class.portal-dragging')
  private isDragging = false;

  @Input()
  public draggingClassList: string[] = [];

  @Input()
  public longPress = false;

  private isLeftButtonDown = false;

  private parent?: HTMLElement | null;

  @ContentChild('draggingAnchor')
  public draggingAnchor?: ElementRef<HTMLElement>;

  @HostBinding('class.portal-drag-ready')
  public isMouseHover?: boolean;
  private isMouseHoverStart?: boolean;

  public constructor(
    private draggingService: BaseDraggingService,
    private element: ElementRef<HTMLElement>
  ) {
    super();
  }

  @HostBinding('class')
  public elementClass = 'portal-drag'; // This should be public (it adds the class portal-drag after adding the directive)

  @HostListener('touchstart', ['$event'])
  public onTouchStart(e: TouchEvent) {
    const firstTouch = e.touches ? e.touches[0] : (e as unknown as { clientX: number; clientY: number }); //Cypress does not correctly assign touches https://github.com/cypress-io/cypress/issues/3808

    this.isLeftButtonDown = true;
    this.isMouseHoverStart = true;
    setTimeout(async () => {
      this.isMouseHover = this.isMouseHoverStart;
      await this.startDragging({
        x: firstTouch.clientX,
        y: firstTouch.clientY
      });
    }, TimerUtils.longPressMs);
  }

  @HostListener('mouseup')
  @HostListener('mouseleave')
  public cancel() {
    this.isLeftButtonDown = false; // cancel dragging before it starts
    this.isMouseHover = false;
    this.isMouseHoverStart = false;
  }

  @HostListener('mouseenter', ['$event'])
  public onMouseenter(event: MouseEvent) {
    this.isMouseHoverStart = true;
    this.isLeftButtonDown = false;
    setTimeout(
      async () => {
        this.isMouseHover = this.isMouseHoverStart; // can drag
        await this.startDragging({
          x: event.clientX,
          y: event.clientY
        });
      },
      this.longPress ? TimerUtils.longPressMs : TimerUtils.humanThresholdMs
    );
  }

  @HostListener('mousedown', ['$event'])
  public async onMousedown(event: MouseEvent) {
    const clickedElement = document.elementFromPoint(event.clientX, event.clientY);

    // If an anchor exists, we can only drag the element by clicking on it
    if (this.draggingAnchor && !this.draggingAnchor.nativeElement?.contains(clickedElement)) {
      return;
    }

    this.isLeftButtonDown = EventUtils.isLeftButtonDown(event);

    await this.startDragging({
      x: event.clientX,
      y: event.clientY
    });
  }

  private async startDragging(position: { x: number; y: number }) {
    if (
      !this.isDraggable ||
      this.isDragging || // is already dragging
      !this.isLeftButtonDown ||
      !this.isMouseHover // Is over the div
    ) {
      return;
    }

    const startX = position.x;
    const startY = position.y;

    const element = this.element.nativeElement;
    element.classList.add(...this.draggingClassList);

    const bounds = element.getBoundingClientRect();
    this.parent = element.parentElement;
    this.isDragging = true;

    document.body.appendChild(element);

    CssUtils.applyDraggingStyle(element.style, bounds);

    this.subscribe(
      this.draggingService
        .startDragging(this.payload || undefined)
        .pipe(
          takeWhile(() => {
            return this.isDragging;
          })
        )
        .subscribe(d => {
          const deltaX = d.x - startX;
          const deltaY = d.y - startY;

          CssUtils.dragTo(element.style, bounds.left + deltaX, bounds.top + deltaY, bounds.height, bounds.width);
        })
    );

    await this.draggingService.nextDraggingEnded();
    this.draggingEnded();
  }

  public draggingEnded() {
    this.isDragging = false;
    const element = this.element.nativeElement;
    element.classList.remove(...this.draggingClassList);

    this.parent?.appendChild(element);
    CssUtils.clearDragStyle(element.style);
  }
}
